/**
 * questionari/normalizza — dal questionario salvato alla forma che usano tutti.
 * ============================================================================
 *
 * - Le opzioni scritte come stringhe (questionari nati prima del 18/10/2026) diventano oggetti con
 *   `id` = il testo stesso: le risposte già salvate contengono quel testo, quindi restano valide.
 * - Il vecchio `richiede_anagrafica` diventa una `SchermataContatti` esplicita con gli stessi campi
 *   e gli stessi obblighi di prima (ragione sociale e partita IVA obbligatorie, il resto no).
 * - `problemiDefinizione` dice che cosa non va in un questionario PRIMA di salvarlo o servirlo:
 *   un difetto nei dati non deve diventare una pagina che si rompe davanti a chi compila.
 *
 * File puro, target ES2017.
 */

import {
  CAMPI_CHIAVE_LEAD,
  CAMPI_CONTATTO,
  TIPI_CON_OPZIONI,
  TIPI_DOMANDA,
  type CampoContatto,
  type Domanda,
  type DomandaSalvata,
  type IstantaneaDomanda,
  type OpzioneDomanda,
  type Questionario,
  type QuestionarioPubblico,
  type QuestionarioSalvato,
  type SchermataContatti,
} from './tipi';
import { codiceLingua } from './traduzioni';

/** Un id di opzione o di domanda nuovo: ASCII minuscolo, cifre e trattino basso. */
export const ID_VALIDO_RE = /^[a-z0-9_]{1,64}$/;

/** Da un'etichetta a un id proponibile («Vetrate panoramiche (VEPA)» → «vetrate_panoramiche_vepa»). */
export function idDaEtichetta(etichetta: string): string {
  const base = etichetta
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
  return base || 'opzione';
}

export function normalizzaOpzione(opzione: string | OpzioneDomanda): OpzioneDomanda {
  if (typeof opzione === 'string') return { id: opzione, etichetta: opzione };
  const normale: OpzioneDomanda = { id: String(opzione.id), etichetta: String(opzione.etichetta ?? opzione.id) };
  if (opzione.esclusiva === true) normale.esclusiva = true;
  return normale;
}

export function normalizzaDomanda(domanda: DomandaSalvata): Domanda {
  const { opzioni, ...resto } = domanda;
  const normale: Domanda = { ...resto, obbligatoria: domanda.obbligatoria === true };
  if (Array.isArray(opzioni)) normale.opzioni = opzioni.map(normalizzaOpzione);
  return normale;
}

/** I campi del vecchio `richiede_anagrafica`, con gli stessi obblighi del flusso di prima. */
function contattiDaAnagraficaStorica(): SchermataContatti {
  const campi: CampoContatto[] = [
    { id: 'azienda', etichetta: 'Ragione sociale', obbligatorio: true },
    { id: 'partita_iva', etichetta: 'Partita IVA', obbligatorio: true },
    { id: 'email', etichetta: 'Email', obbligatorio: false },
    { id: 'telefono', etichetta: 'Telefono', obbligatorio: false },
    { id: 'citta', etichetta: 'Città', obbligatorio: false },
    { id: 'provincia', etichetta: 'Provincia', obbligatorio: false },
  ];
  return { domanda: 'I dati della tua azienda', campi };
}

export function normalizzaQuestionario(q: QuestionarioSalvato): Questionario {
  const domande = Array.isArray(q.domande) ? q.domande.map(normalizzaDomanda) : [];
  let contatti: SchermataContatti | null = null;
  if (q.contatti && Array.isArray(q.contatti.campi)) contatti = q.contatti;
  else if (q.richiede_anagrafica === true) contatti = contattiDaAnagraficaStorica();
  return { ...q, domande, contatti };
}

/**
 * Ciò che arriva alla pagina pubblica: l'email di conferma, le traduzioni e i campi interni restano
 * sul server. `q` è già nella lingua di chi compila (`questionarioInLingua`), `lingua` è quella.
 */
export function proiezionePubblica(
  q: Questionario,
  opts: { lingua: string; link_personale: boolean; sessione_id: string },
): QuestionarioPubblico {
  return {
    titolo: q.titolo,
    descrizione: q.descrizione ?? '',
    lingua: opts.lingua,
    domande: q.domande,
    apertura: q.apertura ?? null,
    // Un link personale conosce già la persona: l'ultima schermata non si chiede.
    contatti: opts.link_personale ? null : q.contatti,
    conferma: q.conferma ?? null,
    link_personale: opts.link_personale,
    sessione_id: opts.sessione_id,
  };
}

/** La copia delle domande da salvare con la risposta (solo ciò che serve a rileggerla). */
export function istantaneaDomande(domande: Domanda[]): IstantaneaDomanda[] {
  return domande.map((d) => {
    const copia: IstantaneaDomanda = { id: d.id, tipo: d.tipo, testo: d.testo };
    if (d.nome_breve) copia.nome_breve = d.nome_breve;
    if (d.opzioni) copia.opzioni = d.opzioni.map((o) => ({ id: o.id, etichetta: o.etichetta }));
    return copia;
  });
}

/** Un difetto della definizione: dove sta e che cosa non va, in italiano (la console è in italiano). */
export interface ProblemaDefinizione {
  domanda_id?: string;
  messaggio: string;
}

/**
 * Che cosa non va in un questionario. Vuoto = si può salvare e servire.
 * Gli id nuovi devono essere ASCII; quelli storici (opzioni-stringa) si accettano come sono.
 */
export function problemiDefinizione(q: QuestionarioSalvato): ProblemaDefinizione[] {
  const problemi: ProblemaDefinizione[] = [];
  if (!q.titolo || !String(q.titolo).trim()) problemi.push({ messaggio: 'Manca il titolo del questionario.' });
  const domande = Array.isArray(q.domande) ? q.domande : [];
  if (domande.length === 0) problemi.push({ messaggio: 'Il questionario non ha domande.' });

  const idDomande = new Set<string>();
  domande.forEach((salvata, indice) => {
    const d = normalizzaDomanda(salvata);
    const dove = d.id || `domanda ${indice + 1}`;
    if (!d.id) problemi.push({ messaggio: `La domanda ${indice + 1} non ha un codice.` });
    else if (idDomande.has(d.id)) problemi.push({ domanda_id: d.id, messaggio: `Il codice «${d.id}» è usato da due domande.` });
    idDomande.add(d.id);
    if ((TIPI_DOMANDA as readonly string[]).indexOf(d.tipo) < 0) {
      problemi.push({ domanda_id: d.id, messaggio: `La domanda «${dove}» ha un tipo sconosciuto («${d.tipo}»).` });
      return;
    }
    if (!d.testo || !d.testo.trim()) problemi.push({ domanda_id: d.id, messaggio: `La domanda «${dove}» non ha il testo.` });

    if (TIPI_CON_OPZIONI.indexOf(d.tipo) >= 0) {
      const opzioni = d.opzioni ?? [];
      if (opzioni.length < 2) problemi.push({ domanda_id: d.id, messaggio: `La domanda «${dove}» ha meno di due opzioni.` });
      const idOpzioni = new Set<string>();
      opzioni.forEach((o) => {
        if (!o.id) problemi.push({ domanda_id: d.id, messaggio: `Un'opzione di «${dove}» non ha un codice.` });
        if (idOpzioni.has(o.id)) problemi.push({ domanda_id: d.id, messaggio: `Il codice di opzione «${o.id}» è ripetuto in «${dove}».` });
        idOpzioni.add(o.id);
        if (!o.etichetta || !o.etichetta.trim()) problemi.push({ domanda_id: d.id, messaggio: `Un'opzione di «${dove}» non ha il testo.` });
        if (o.esclusiva && d.tipo !== 'scelta_multipla') {
          problemi.push({ domanda_id: d.id, messaggio: `«${o.etichetta}» è esclusiva, ma «${dove}» non è a scelta multipla.` });
        }
      });
      if (d.massimo_scelte !== undefined) {
        if (d.tipo !== 'scelta_multipla') {
          problemi.push({ domanda_id: d.id, messaggio: `Il massimo di scelte vale solo per le scelte multiple («${dove}»).` });
        } else if (!Number.isInteger(d.massimo_scelte) || d.massimo_scelte < 1 || d.massimo_scelte > opzioni.length) {
          problemi.push({ domanda_id: d.id, messaggio: `Il massimo di scelte di «${dove}» deve stare fra 1 e il numero di opzioni.` });
        }
      }
      if (d.campo_dettaglio) {
        const mostra = Array.isArray(d.campo_dettaglio.mostra_se) ? d.campo_dettaglio.mostra_se : [];
        if (!d.campo_dettaglio.etichetta || !d.campo_dettaglio.etichetta.trim()) {
          problemi.push({ domanda_id: d.id, messaggio: `Il campo che compare in «${dove}» non ha l'etichetta.` });
        }
        if (mostra.length === 0) problemi.push({ domanda_id: d.id, messaggio: `Il campo che compare in «${dove}» non dice con quali opzioni.` });
        mostra.forEach((id) => {
          if (!idOpzioni.has(id)) problemi.push({ domanda_id: d.id, messaggio: `Il campo che compare in «${dove}» cita un'opzione che non esiste («${id}»).` });
        });
      }
    } else {
      if (d.opzioni && d.opzioni.length > 0) problemi.push({ domanda_id: d.id, messaggio: `La domanda «${dove}» non è a scelta ma ha delle opzioni.` });
      if (d.campo_dettaglio) problemi.push({ domanda_id: d.id, messaggio: `Il campo che compare vale solo per le domande a scelta («${dove}»).` });
    }

    if (d.tipo === 'numero') {
      const minimo = d.minimo ?? 0;
      if (!Number.isInteger(minimo)) problemi.push({ domanda_id: d.id, messaggio: `Il minimo di «${dove}» deve essere un intero.` });
      if (d.massimo !== undefined && (!Number.isInteger(d.massimo) || d.massimo < minimo)) {
        problemi.push({ domanda_id: d.id, messaggio: `Il massimo di «${dove}» deve essere un intero non più piccolo del minimo.` });
      }
      (d.scorciatoie ?? []).forEach((s) => {
        if (!Number.isInteger(s.valore) || s.valore < minimo || (d.massimo !== undefined && s.valore > d.massimo)) {
          problemi.push({ domanda_id: d.id, messaggio: `Il tasto rapido «${s.etichetta ?? s.valore}» di «${dove}» è fuori dai limiti.` });
        }
      });
    } else if (d.scorciatoie && d.scorciatoie.length > 0) {
      problemi.push({ domanda_id: d.id, messaggio: `I tasti rapidi valgono solo per le domande numeriche («${dove}»).` });
    }
  });

  // I salti vanno solo in avanti, verso domande che esistono.
  domande.forEach((d, indice) => {
    if (!d.salta_a) return;
    const arrivo = domande.findIndex((x) => x.id === d.salta_a!.vai_a_domanda_id);
    if (arrivo <= indice) {
      problemi.push({ domanda_id: d.id, messaggio: `Il salto di «${d.id}» deve portare a una domanda che viene dopo.` });
    }
  });

  if (q.contatti) {
    const campi = Array.isArray(q.contatti.campi) ? q.contatti.campi : [];
    if (!q.contatti.domanda || !q.contatti.domanda.trim()) problemi.push({ messaggio: "L'ultima schermata non ha la domanda." });
    const visti = new Set<string>();
    campi.forEach((c) => {
      if ((CAMPI_CONTATTO as readonly string[]).indexOf(c.id) < 0) problemi.push({ messaggio: `Campo sconosciuto nell'ultima schermata («${c.id}»).` });
      if (visti.has(c.id)) problemi.push({ messaggio: `Il campo «${c.id}» è ripetuto nell'ultima schermata.` });
      visti.add(c.id);
      if (!c.etichetta || !c.etichetta.trim()) problemi.push({ messaggio: `Il campo «${c.id}» dell'ultima schermata non ha l'etichetta.` });
    });
    const chiaveObbligatoria = campi.some((c) => c.obbligatorio && CAMPI_CHIAVE_LEAD.indexOf(c.id) >= 0);
    if (!chiaveObbligatoria) {
      problemi.push({ messaggio: "Nell'ultima schermata l'email o la partita IVA devono essere obbligatorie: servono a riconoscere chi risponde." });
    }
    const consenso = q.contatti.consenso;
    if (consenso) {
      if (!consenso.testo || !consenso.testo.trim()) problemi.push({ messaggio: 'Il consenso non ha il testo.' });
      if (!consenso.versione || !consenso.versione.trim()) problemi.push({ messaggio: 'Il consenso non ha la versione del testo.' });
      if (!Array.isArray(consenso.canali) || consenso.canali.length === 0) problemi.push({ messaggio: 'Il consenso non dice i canali.' });
      if (consenso.canali && consenso.canali.indexOf('email') >= 0 && !visti.has('email')) {
        problemi.push({ messaggio: "Il consenso cita l'email, ma l'ultima schermata non la chiede." });
      }
      if (consenso.url_informativa && !/^https:\/\//.test(consenso.url_informativa)) {
        problemi.push({ messaggio: "L'indirizzo dell'informativa deve cominciare con https://." });
      }
    }
  }

  const email = q.email_conferma;
  if (email && email.attiva) {
    if (!q.contatti || !q.contatti.campi.some((c) => c.id === 'email')) {
      problemi.push({ messaggio: "L'email di conferma è attiva, ma il questionario non chiede l'email." });
    }
    if (!email.oggetto || !email.titolo || !email.testo || !email.perche) {
      problemi.push({ messaggio: "All'email di conferma mancano oggetto, titolo, testo o perché si riceve." });
    }
    if (email.rispondi_a && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.rispondi_a)) {
      problemi.push({ messaggio: "L'indirizzo per le risposte all'email di conferma non è valido." });
    }
    if (q.contatti && q.contatti.consenso && (!email.testo_consenso || !email.bottone_consenso)) {
      problemi.push({ messaggio: "C'è il consenso agli aggiornamenti: l'email di conferma deve avere il testo e il bottone per confermarlo." });
    }
  }
  if (q.lingua_sorgente !== undefined && q.lingua_sorgente !== null && !codiceLingua(q.lingua_sorgente)) {
    problemi.push({ messaggio: 'La lingua dei testi deve essere un codice di due lettere (it, en…).' });
  }
  Object.keys(q.traduzioni || {}).forEach((l) => {
    if (!/^[a-z]{2}$/.test(l)) problemi.push({ messaggio: `Le traduzioni hanno una lingua non valida («${l}»).` });
    const voce = (q.traduzioni || {})[l];
    if (voce !== undefined && (voce === null || typeof voce !== 'object' || Array.isArray(voce))) {
      problemi.push({ messaggio: `Le traduzioni in «${l}» devono essere un insieme di testi, non un testo solo.` });
    }
  });
  if (q.conferma && q.conferma.collegamento && !/^https:\/\//.test(q.conferma.collegamento.url)) {
    problemi.push({ messaggio: "Il collegamento della conferma deve cominciare con https://." });
  }
  return problemi;
}
